import { ensureArray } from "types";

import { get, IHttpRequestConfig, post, put, remove } from "@/browser";
import { applyHeaders, extractDataFromResponse, parseResponse } from "@/core/api";
import { compose, IURLSearchParams } from "@/core/url";
import { JobApplicationCostsDto } from "@/data/job-applications/types/JobApplicationCostsDto";
import { LifecyclePageable } from "@/data/lifecycle";

import {
  IJobApplication,
  IPartialPageableJobApplication,
  jobApplicationType,
  pageableJobApplicationType,
} from "./types";
import { JobApplication } from "./types/JobApplication";

const API = {
  list: "/api/job-requests/{jobRequestId}/job-applications",
  post: "/api/job-requests/{jobRequestId}/job-applications",
  saveDraft: "/api/job-requests/{jobRequestId}/job-applications/drafts",
  updateDraft: "/api/job-applications/drafts/{jobApplicationId}",
  allList: "/api/job-applications",
  allListCompact: "/api/job-applications/short",
  get: "/api/job-applications/{jobApplicationId}",
  confirm: "/api/job-applications/{jobApplicationId}/confirm",
  reject: "/api/job-applications/{jobApplicationId}/reject",
  delete: "/api/job-applications/{jobApplicationId}",
  statusCount: "api/job-applications/status-count",
  profileDrafts: "/api/job-applications/drafts",
  lifecycle: "/api/job-applications/{jobApplicationId}/lifecycle",
};

export const MOTIVATION_MAX_LENGTH = 5000;
export const ATTACHMENTS_MAX_NUMBER = 10;
export const ELABORATION_MAX_LENGTH = 1000;
export const PHONE_NUMBER_MAX_LENGTH = 50;

export interface FetchJobRequestApplicationsParams {
  jobRequestId: number;
  companyId?: number;
}

export const fetchJobRequestApplications = (
  params: FetchJobRequestApplicationsParams,
  options?: IHttpRequestConfig,
) => {
  const { jobRequestId, companyId, ...search } = params;

  const url = compose(API.list, { jobRequestId }, search);

  return get<IPartialPageableJobApplication>(url, applyHeaders({ companyId }, options))
    .then(extractDataFromResponse)
    .then(pageableJobApplicationType);
};

interface SaveDraftJobApplicationParams {
  jobRequestId: number;
  companyId?: number;
}

export const saveDraftJobApplication = (
  params: SaveDraftJobApplicationParams,
  data: JobApplication,
  options?: IHttpRequestConfig,
) => {
  const { jobRequestId, companyId } = params;

  const url = compose(API.saveDraft, { jobRequestId });

  return post(url, data, applyHeaders({ companyId }, options)).then(parseResponse<JobApplication>(JobApplication));
};

interface UpdateDraftJobApplicationParams {
  jobApplicationId: number;
  companyId?: number;
}

export const updateDraftJobApplication = (
  params: UpdateDraftJobApplicationParams,
  data: JobApplication,
  options?: IHttpRequestConfig,
) => {
  const { jobApplicationId, companyId } = params;

  const url = compose(API.updateDraft, { jobApplicationId });

  return put(url, data, applyHeaders({ companyId }, options)).then(parseResponse<JobApplication>(JobApplication));
};

interface ConfirmJobApplicationParams {
  jobApplicationId: number;
  companyId?: number;
}

export const confirmJobApplication = (
  params: ConfirmJobApplicationParams,
  data: JobApplication,
  options?: IHttpRequestConfig,
) => {
  const { jobApplicationId, companyId } = params;

  const url = compose(API.confirm, { jobApplicationId });

  return put(url, data, applyHeaders({ companyId }, options)).then(parseResponse<JobApplication>(JobApplication));
};

interface DeleteJobRequestApplicationParams {
  jobApplicationId: number;
  companyId?: number;
}

export const deleteApplication = (params: DeleteJobRequestApplicationParams, options?: IHttpRequestConfig) => {
  const { jobApplicationId, companyId } = params;

  const url = compose(API.delete, { jobApplicationId });

  return remove(url, {}, applyHeaders({ companyId }, options)).then(extractDataFromResponse).then(jobApplicationType);
};

export interface FetchJobApplicationsParams extends IURLSearchParams {
  companyId?: number;
}

export const fetchJobApplications = (params: FetchJobApplicationsParams, options?: IHttpRequestConfig) => {
  const { companyId, ...search } = params;

  const url = compose(API.allList, {}, search);

  return get<IPartialPageableJobApplication>(url, applyHeaders({ companyId }, options))
    .then(extractDataFromResponse)
    .then(pageableJobApplicationType);
};

export const fetchJobApplicationsCompact = (params: FetchJobApplicationsParams, options?: IHttpRequestConfig) => {
  const { ...search } = params;

  const url = compose(API.allListCompact, {}, search);

  return get<IPartialPageableJobApplication>(url, applyHeaders({}, options))
    .then(extractDataFromResponse)
    .then(pageableJobApplicationType);
};

export interface FetchJobApplicationParams {
  jobApplicationId: number;
  companyId?: number;
}

/**
 * @deprecated Use fetchJobApplication instead.
 */
export const fetchJobApplicationEnsure = (params: FetchJobApplicationParams, options?: IHttpRequestConfig) => {
  const { companyId, ...pathParams } = params;

  const url = compose(API.get, pathParams);

  return get<IJobApplication>(url, applyHeaders({ companyId }, options))
    .then(extractDataFromResponse)
    .then(jobApplicationType);
};

export interface FetchJobApplicationParams {
  jobApplicationId: number;
  companyId?: number;
}

export const fetchJobApplication = (params: FetchJobApplicationParams, options?: IHttpRequestConfig) => {
  const { companyId, jobApplicationId, ...restParams } = params;

  const url = compose(API.get, { jobApplicationId }, restParams);

  return get(url, applyHeaders({ companyId }, options)).then(parseResponse<JobApplication>(JobApplication));
};

export interface FetchProfileDraftApplicationsParams {
  profileId: number;
  jobRequestId: number;
  companyId?: number;
}

export const fetchProfileDraftApplications = (
  params: FetchProfileDraftApplicationsParams,
  options?: IHttpRequestConfig,
) => {
  const { companyId, ...searchParams } = params;

  const url = compose(API.profileDrafts, {}, searchParams);

  return get<IJobApplication[]>(url, applyHeaders({ companyId }, options))
    .then(extractDataFromResponse)
    .then(ensureArray(jobApplicationType));
};

export interface FetchJobApplicationLifecycleParams extends IURLSearchParams {
  jobApplicationId: number;
  companyId?: number;
}

export const fetchJobApplicationLifecycle = (
  params: FetchJobApplicationLifecycleParams,
  options?: IHttpRequestConfig,
) => {
  const { companyId, jobApplicationId, ...restParams } = params;

  const url = compose(
    API.lifecycle,
    { jobApplicationId },
    {
      ...restParams,
      sortConfig: [
        {
          sortBy: "createdDate",
          order: "DESC",
        },
      ],
    },
  );

  return get(url, applyHeaders({ companyId }, options)).then(parseResponse<LifecyclePageable>(LifecyclePageable));
};

export interface JRAPayExpressValues extends JobApplicationCostsDto {
  payExpressFeePercentage: number | null;
  supplierPaymentTerm: number | null;
  payExpressSelected: boolean;
}

/**
 * Extracts pay express info from JRA. Takes PE fee percentage and payment term from old PE object on JRA if they exist.
 * If not, those are extracted from pay express config on job request.
 * @param jobApplication
 * @return JRAPayExpressValues
 */
export const extractPayExpressInfoFromJRA = (jobApplication: JobApplication): JRAPayExpressValues => {
  return {
    ...jobApplication.costs,
    supplierPaymentTerm:
      jobApplication.payExpress?.paymentTerm ?? jobApplication.jobRequest.supplierPaymentTerm ?? null,
    payExpressFeePercentage:
      jobApplication.payExpress?.payExpressFeePercentage ??
      jobApplication.jobRequest.payExpressConfig?.payExpressFeePercentage ??
      null,
    payExpressSelected:
      jobApplication?.payExpressSelected ?? jobApplication.payExpress?.chosenPricingMethod === "DECREASE" ?? false,
  };
};
